import { useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { parseRecipientCsv, type CsvParseResult } from '../lib/csv';

/** A minimal, valid recipient file for staff to download and edit. */
const SAMPLE_CSV =
  [
    'Email,Name',
    'jane@example.com,Jane Doe',
    'john@example.com,John Smith',
    'guest@example.com,Guest Name',
  ].join('\n') + '\n';

function downloadSampleCsv(): void {
  const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'sample-recipients.csv';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

interface RecipientUploadProps {
  result: CsvParseResult | null;
  onParsed: (result: CsvParseResult, fileName: string) => void;
}

const PREVIEW_ROWS = 5;
const PROBLEM_ROWS = 10;

export default function RecipientUpload({ result, onParsed }: RecipientUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [readError, setReadError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setReadError(null);
    try {
      const text = await file.text();
      const parsed = parseRecipientCsv(text);
      setFileName(file.name);
      onParsed(parsed, file.name);
    } catch (error) {
      setReadError(error instanceof Error ? error.message : 'Could not read that file');
    }
  }

  const problems = result ? [...result.errors, ...result.duplicates] : [];

  return (
    <Stack spacing={3}>
      <Box>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handleFile(file);
            // Reset so re-picking the same file still fires a change event.
            event.target.value = '';
          }}
        />
        <Button variant="contained" onClick={() => inputRef.current?.click()}>
          Choose CSV file
        </Button>
        {fileName && (
          <Typography variant="body2" component="span" sx={{ ml: 2 }}>
            {fileName}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Needs a column headed <strong>Email</strong> — the heading is not case-sensitive, and
          E-mail or Email Address work too. Any other column — like <strong>Name</strong> — becomes
          a merge field you can drop into the message with the <em>Insert merge field</em> button.{' '}
          <Link component="button" type="button" onClick={downloadSampleCsv}>
            Download a sample CSV
          </Link>{' '}
          to edit.
        </Typography>
      </Box>

      {readError && <Alert severity="error">{readError}</Alert>}

      {result && (
        <>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip color="success" label={`${result.rows.length} ready to send`} />
            {result.duplicates.length > 0 && (
              <Chip color="warning" label={`${result.duplicates.length} duplicates removed`} />
            )}
            {result.errors.length > 0 && (
              <Chip color="error" label={`${result.errors.length} rows skipped`} />
            )}
          </Stack>

          {result.rows.length === 0 && (
            <Alert severity="error">
              <AlertTitle>No usable rows</AlertTitle>
              Nothing in this file can be sent. Check that there is an <strong>Email</strong>{' '}
              column and at least one valid address.
            </Alert>
          )}

          {result.rows.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <caption style={{ captionSide: 'bottom', padding: '8px 16px' }}>
                  First {Math.min(PREVIEW_ROWS, result.rows.length)} of {result.rows.length}{' '}
                  recipients.
                </caption>
                <TableHead>
                  <TableRow>
                    <TableCell component="th" scope="col">
                      Email
                    </TableCell>
                    {result.fields
                      .filter((field) => field !== 'email')
                      .map((field) => (
                        <TableCell key={field} component="th" scope="col">
                          {field}
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.rows.slice(0, PREVIEW_ROWS).map((row) => (
                    <TableRow key={row.email}>
                      <TableCell>{row.email}</TableCell>
                      {result.fields
                        .filter((field) => field !== 'email')
                        .map((field) => (
                          <TableCell key={field}>{row.mergeData[field] ?? ''}</TableCell>
                        ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {problems.length > 0 && (
            <Box>
              <Typography variant="subtitle1" component="h3" gutterBottom>
                Rows that will not be sent
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell component="th" scope="col">
                        Row
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Value
                      </TableCell>
                      <TableCell component="th" scope="col">
                        Reason
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {problems.slice(0, PROBLEM_ROWS).map((problem) => (
                      <TableRow key={`${problem.rowNumber}-${problem.value}`}>
                        <TableCell>{problem.rowNumber}</TableCell>
                        <TableCell>{problem.value || '—'}</TableCell>
                        <TableCell>{problem.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {problems.length > PROBLEM_ROWS && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  …and {problems.length - PROBLEM_ROWS} more.
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}
